import "whatwg-fetch"; // polyfill for older browsers
import {
  getAPIConfig,
  convertQueryObjToString,
  handleAPIResponse,
} from "@utils/browser-helpers";
import { executeAPIRequest } from "@adapters/api";
import type { Client, ClientGroup } from "@types";

interface C3Adapter {
  clientManagement: {
    fetchBaseClients: () => Promise<any>;
    fetchAllClients: (fetchConfig?: any) => Promise<any>;
    fetchIndividualClient: (clientId: string | number) => Promise<any>;
    createClient: (changes: Partial<Client>) => Promise<any>;
    disableIndividualClient: (clientId: string | number) => Promise<any>;
    addClientInformation: (changes: any) => Promise<any>;
    fetchAllClientGroups: () => Promise<any>;
    fetchIndividualClientGroup: (clientId: string | number) => Promise<any>;
    createClientGroup: (changes: Partial<ClientGroup>) => Promise<any>;
    patchClientGroup: (id: string | number, changes: Partial<ClientGroup>) => Promise<any>;
    fetchCertificateDetails: (ctid: string | number) => Promise<any>;
    disableClientGroup: (id: string | number) => Promise<any>;
  };
  featureManagement: {
    copyAppSettings: (sourceCTID: string | number, editCTID: string | number) => Promise<any>;
    patchClientFeatures: (changes: any) => Promise<any>;
    patchAdvancedClientSettings: (changes: any) => Promise<any>;
    postCertMetadata: (changes: any) => Promise<any>;
    createActionType: (changes: any) => Promise<any>;
    createNotificationChannel: (changes: any) => Promise<any>;
    deleteNotificationChannel: (ctid: string | number, channelId: string | number) => Promise<any>;
    patchBannerPosition: (changes: any) => Promise<any>;
    deleteBannerPosition: (ctid: string | number, configid: string | number) => Promise<any>;
  };
}

const c3Adapter: C3Adapter = {
  clientManagement: {
    fetchBaseClients: async (): Promise<any> => {
      const postRoute = `/clients/base`;
      const apiConfig = getAPIConfig(postRoute);
      const response = await executeAPIRequest(apiConfig, "GET");
      const responseData = await response.json();
      return handleAPIResponse(responseData);
    },

    fetchAllClients: async (fetchConfig: any = ""): Promise<any> => {
      const route = `/clients${convertQueryObjToString(fetchConfig)}`;
      const apiConfig = getAPIConfig(route);
      const response = await executeAPIRequest(apiConfig, "GET");
      const responseData = await response.json();
      return handleAPIResponse(responseData, []);
    },

    fetchIndividualClient: async (clientId: string | number): Promise<any> => {
      const route = `/clients/id/${clientId}`;
      const apiConfig = getAPIConfig(route);
      const response = await executeAPIRequest(apiConfig, "GET");
      const responseData = await response.json();
      return handleAPIResponse(responseData, []);
    },

    createClient: async (changes: Partial<Client>): Promise<any> => {
      const postRoute = `/clients/base`;
      const apiConfig = getAPIConfig(postRoute);
      const response = await executeAPIRequest(
        apiConfig,
        "POST",
        {},
        changes
      );
      const responseData = await response.json();
      return handleAPIResponse(responseData);
    },

    disableIndividualClient: async (clientId: string | number): Promise<any> => {
      const route = `/clients/${clientId}`;
      const apiConfig = getAPIConfig(route);
      const response = await executeAPIRequest(apiConfig, "DELETE", {}, {});
      const responseData = await response.json();
      return handleAPIResponse(responseData, []);
    },

    addClientInformation: async (changes: any): Promise<any> => {
      const postRoute = `/clients/client_information`;
      const apiConfig = getAPIConfig(postRoute);
      const response = await executeAPIRequest(
        apiConfig,
        "POST",
        {},
        changes
      );
      const responseData = await response.json();
      return handleAPIResponse(responseData);
    },

    fetchAllClientGroups: async (): Promise<any> => {
      const route = `/clients/client_group`;
      const apiConfig = getAPIConfig(route);
      const response = await executeAPIRequest(apiConfig, "GET");
      const responseData = await response.json();
      return handleAPIResponse(responseData, []);
    },

    fetchIndividualClientGroup: async (clientId: string | number): Promise<any> => {
      const route = `/clients/client_group/id/${clientId}`;
      const apiConfig = getAPIConfig(route);
      const response = await executeAPIRequest(apiConfig, "GET");
      const responseData = await response.json();
      return handleAPIResponse(responseData);
    },

    createClientGroup: async (changes: Partial<ClientGroup>): Promise<any> => {
      const route = `/clients/client_group`;
      const apiConfig = getAPIConfig(route);
      const response = await executeAPIRequest(
        apiConfig,
        "POST",
        {},
        changes
      );
      const responseData = await response.json();
      return handleAPIResponse(responseData);
    },

    patchClientGroup: async (id: string | number, changes: Partial<ClientGroup>): Promise<any> => {
      const patchRoute = `/clients/client_group/id/${id}`;
      const apiConfig = getAPIConfig(patchRoute);
      const response = await executeAPIRequest(
        apiConfig,
        "PATCH",
        {},
        changes
      );
      const responseData = await response.json();
      return handleAPIResponse(responseData, []);
    },
    fetchCertificateDetails: async (ctid: string | number): Promise<any> => {
      const route = `/clients/download_cert/${ctid}`;
      const apiConfig = getAPIConfig(route);
      const response = await executeAPIRequest(apiConfig, "GET");
      const responseData = await response.json();
      return handleAPIResponse(responseData, []);
    },

    disableClientGroup: async (id: string | number): Promise<any> => {
      const route = `/clients/client_group/id/${id}`;
      const apiConfig = getAPIConfig(route);
      const response = await executeAPIRequest(apiConfig, "DELETE", {}, {});
      const responseData = await response.json();
      return handleAPIResponse(responseData, []);
    },
  },

  featureManagement: {
    copyAppSettings: async (sourceCTID: string | number, editCTID: string | number): Promise<any> => {
      const postRoute = `/appconfig/copy/${sourceCTID}/${editCTID}`;
      const apiConfig = getAPIConfig(postRoute);
      const headers = {
        "Content-Type": "application/json",
        Authorization: "Bearer " + apiConfig.jwtToken,
      };
      const response = await executeAPIRequest(apiConfig, "POST", headers, {});
      const responseData = await response.json();
      return handleAPIResponse(responseData);
    },

    patchClientFeatures: async (changes: any): Promise<any> => {
      const patchRoute = "/client_features";
      const apiConfig = getAPIConfig(patchRoute);
      const response = await executeAPIRequest(
        apiConfig,
        "PATCH",
        {},
        changes
      );
      const responseData = await response.json();
      return handleAPIResponse(responseData);
    },

    patchAdvancedClientSettings: async (changes: any): Promise<any> => {
      const patchRoute = "/client_advanced_settings";
      const apiConfig = getAPIConfig(patchRoute);
      const response = await executeAPIRequest(
        apiConfig,
        "PATCH",
        {},
        changes
      );
      const responseData = await response.json();
      return handleAPIResponse(responseData);
    },

    postCertMetadata: async (changes: any): Promise<any> => {
      const patchRoute = "/client_advanced_settings/cert";
      const apiConfig = getAPIConfig(patchRoute);
      const response = await executeAPIRequest(
        apiConfig,
        "POST",
        {},
        changes
      );
      const responseData = await response.json();
      return handleAPIResponse(responseData);
    },

    createActionType: async (changes: any): Promise<any> => {
      const postRoute = `/client_action_types`;
      const apiConfig = getAPIConfig(postRoute);
      const response = await executeAPIRequest(
        apiConfig,
        "POST",
        {},
        changes
      );
      const responseData = await response.json();
      return handleAPIResponse(responseData);
    },

    createNotificationChannel: async (changes: any): Promise<any> => {
      const postRoute = `/notifications/notification_channel`;
      const apiConfig = getAPIConfig(postRoute);
      const response = await executeAPIRequest(
        apiConfig,
        "POST",
        {},
        changes
      );
      const responseData = await response.json();
      return handleAPIResponse(responseData);
    },

    deleteNotificationChannel: async (ctid: string | number, channelId: string | number): Promise<any> => {
      const route = `/notifications/notification_channel?clienttype_id=${ctid}&channel_id=${channelId}`;
      const apiConfig = getAPIConfig(route);
      const response = await executeAPIRequest(apiConfig, "DELETE");
      const responseData = await response.json();
      return handleAPIResponse(responseData, []);
    },

    patchBannerPosition: async (changes: any): Promise<any> => {
      const route = `/banners/config/`;
      const apiConfig = getAPIConfig(route);
      const response = await executeAPIRequest(apiConfig, "PUT", {}, changes);
      const responseData = await response.json();
      return handleAPIResponse(responseData);
    },

    deleteBannerPosition: async (ctid: string | number, configid: string | number): Promise<any> => {
      const route = `/banners/config?ctid=${ctid}&config_id=${configid}`;
      const apiConfig = getAPIConfig(route);
      const response = await executeAPIRequest(apiConfig, "DELETE");
      const responseData = await response.json();
      return handleAPIResponse(responseData);
    },
  },
};

declare global {
  interface Window {
    c3Adapter: C3Adapter;
  }
}

window.c3Adapter = c3Adapter;

export default c3Adapter;
